import { useState, useSyncExternalStore, useRef, useLayoutEffect } from 'react'
import { useAutoRetry } from '../../hooks/useAutoRetry'
import ErrorRetryBar from '../../components/ErrorRetryBar'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Checkbox,
  Text,
  Spinner,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { ArrowSync24Regular } from '@fluentui/react-icons'
import { ActionButtons } from '../../components/ActionButtons'
import { absenceRequestsService, AbsenceRequestStatus, DayHalf, DAY_HALF_LABEL } from '../../services/absenceRequestsService'
import { vacationBalanceService } from '../../services/vacationBalanceService'
import { useAuth } from '../../auth/useAuth'

function useIsMobile(breakpoint = 640) {
  return useSyncExternalStore(
    cb => { window.addEventListener('resize', cb); return () => window.removeEventListener('resize', cb) },
    () => window.innerWidth <= breakpoint,
    () => false,
  )
}

// Měří skutečnou šířku obsahu (reaguje na šíři tabulky, ne celého okna — levé menu mění šířku)
function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null)
  const [width, setWidth] = useState(0)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) setWidth(entry.contentRect.width)
    })
    ro.observe(el)
    setWidth(el.clientWidth)
    return () => ro.disconnect()
  }, [])
  return [ref, width] as const
}

// "11. 6. 2026 – 12. 6. 2026" → "11. 6. – 12. 6. 2026" (rok u začátku vynechán, je-li shodný s koncem)
function formatDateRange(startIso: string, endIso: string) {
  const start = new Date(startIso)
  const end = new Date(endIso)
  const startStr = start.getFullYear() === end.getFullYear()
    ? start.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' })
    : start.toLocaleDateString('cs-CZ')
  return `${startStr} – ${end.toLocaleDateString('cs-CZ')}`
}

// "2" → "2 dny", "1" → "1 den", "0.5" → "0,5 dne", "5" → "5 dní"
function formatDays(n: number) {
  const s = String(n).replace('.', ',')
  const unit = n === 1 ? 'den' : n < 1 ? 'dne' : Number.isInteger(n) && n <= 4 ? 'dny' : 'dní'
  return `${s} ${unit}`
}

const useStyles = makeStyles({
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  headerActions: { display: 'flex', gap: '8px', alignItems: 'center' },
  th: {
    fontSize: '11px', fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3, textTransform: 'uppercase',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  tableRow: {
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    cursor: 'default',
  },
  nowrap: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  thWrap: {
    fontSize: '11px', fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3, textTransform: 'uppercase',
    whiteSpace: 'normal', lineHeight: '1.1',
  },
  actions: { display: 'flex', gap: '4px' },
  resultList: { margin: '8px 0 0', paddingLeft: '16px', fontSize: '13px', color: tokens.colorNeutralForeground2 },
  // Mobilní karty (varianta 1) — #7
  mobileList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  card: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px 14px',
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  cardName: { fontSize: '15px', fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  cardMeta: { display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '13px', color: tokens.colorNeutralForeground2 },
  cardMetaStrong: { color: tokens.colorNeutralForeground1, fontWeight: tokens.fontWeightSemibold },
  cardActions: { display: 'flex', gap: '8px', marginTop: '2px' },
  cardEmpty: { textAlign: 'center', color: tokens.colorNeutralForeground3, padding: '24px 0' },
})

export default function AbsencesPage() {
  const styles = useStyles()
  const isMobile = useIsMobile()
  const [tableWrapRef, contentWidth] = useElementWidth<HTMLDivElement>()
  // Pod touto šíří OBSAHU tabulky (ne celého okna) se tlačítka Schválit/Zamítnout zúží jen na ikonu (laditelné)
  const iconOnlyActions = contentWidth > 0 && contentWidth <= 1050
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const currentYear = new Date().getFullYear()
  const [recalcDialogOpen, setRecalcDialogOpen] = useState(false)
  const [recalcConfirmOpen, setRecalcConfirmOpen] = useState(false)
  const [recalcYear, setRecalcYear] = useState(currentYear + 1)
  const [recalcEntitlements, setRecalcEntitlements] = useState(false)
  const [recalcResult, setRecalcResult] = useState<{ year: number; employeesProcessed: number; recalcEntitlements: boolean; errors: string[] } | null>(null)

  const { isHR, isAdmin } = useAuth()
  // Manažer (bez HR/Admin role) vidí jen svůj tým; HR/Admin vidí vše
  const myTeamOnly = !isHR && !isAdmin

  const { data: requests, isLoading, isError, refetch } = useQuery({
    queryKey: ['absences', 'pending', myTeamOnly],
    queryFn: () => absenceRequestsService.getAll({
      status: AbsenceRequestStatus.Pending,
      myTeamOnly,
    }),
  })

  const { autoRetryCount, autoRetryCountdown, maxRetries, resetAndRetry } = useAutoRetry(isError, refetch)

  const decideMutation = useMutation({
    mutationFn: ({ id, approved }: { id: number; approved: boolean }) =>
      absenceRequestsService.decide(id, approved, null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['absences'] })
      queryClient.invalidateQueries({ queryKey: ['vacation-balance'] })
    },
    onError: (err: any) => {
      const status = err?.response?.status
      if (status === 403)
        alert('Nemáte oprávnění ke schválení této žádosti. Ujistěte se, že jste přiřazen jako nadřízený daného zaměstnance.')
      else if (status === 409)
        alert(err?.response?.data ?? 'Žádost nelze v tomto stavu rozhodnout.')
      else
        alert('Schválení se nezdařilo. Zkuste to prosím znovu.')
    },
  })

  const recalcMutation = useMutation({
    mutationFn: () => vacationBalanceService.recalculateAll(recalcYear, recalcEntitlements),
    onSuccess: (result) => {
      setRecalcConfirmOpen(false)
      setRecalcResult(result)
      setRecalcDialogOpen(true)
      queryClient.invalidateQueries({ queryKey: ['vacation-balance'] })
    },
    onError: () => alert('Přepočet se nezdařil.'),
  })

  return (
    <div>
      <div className={styles.header}>
        <Text size={600} weight="semibold">
          {t('absences.title')} – {t('absences.status.1')}
        </Text>
        <div className={styles.headerActions}>
          <Button
            appearance="subtle"
            icon={<ArrowSync24Regular />}
            onClick={() => { setRecalcYear(currentYear + 1); setRecalcConfirmOpen(true) }}
          >
            Přepočítat dovolenou
          </Button>
        </div>
      </div>

      <ErrorRetryBar
        isError={isError}
        message="Nepodařilo se načíst nepřítomnosti."
        autoRetryCount={autoRetryCount}
        autoRetryCountdown={autoRetryCountdown}
        maxRetries={maxRetries}
        onRetry={resetAndRetry}
      />

      {isLoading ? (
        <Spinner label={t('app.loading')} />
      ) : isMobile ? (
        <div className={styles.mobileList}>
          {requests?.map(r => (
            <div key={r.id} className={styles.card}>
              <div className={styles.cardName}>{r.employeeFullName}</div>
              <div className={styles.cardMeta}>
                <span className={styles.cardMetaStrong}>{r.absenceTypeName}</span>
                <span>{formatDateRange(r.startDate, r.endDate)} · {formatDays(r.totalDays)}{r.dayHalf !== DayHalf.Full ? ` (${DAY_HALF_LABEL[r.dayHalf]})` : ''}</span>
                {(r.location || r.note) && (
                  <span>{r.location ?? '–'}{r.note ? ` (${r.note})` : ''}</span>
                )}
              </div>
              <div className={styles.cardActions}>
                <ActionButtons
                  onApprove={() => decideMutation.mutate({ id: r.id, approved: true })}
                  onReject={() => decideMutation.mutate({ id: r.id, approved: false })}
                  isLoading={decideMutation.isPending}
                  iconOnly={false}
                />
              </div>
            </div>
          ))}
          {requests?.length === 0 && (
            <div className={styles.cardEmpty}>Žádné žádosti čekající na schválení</div>
          )}
        </div>
      ) : (
        <div ref={tableWrapRef} style={{ width: '100%', overflowX: 'auto' }}>
        <Table style={{
          width: '100%',
          gridTemplateColumns: isMobile
            ? `minmax(0, 1fr) 168px ${iconOnlyActions ? '124px' : '230px'}`
            : `minmax(110px, 1fr) 110px 168px 52px minmax(0, 1fr) ${iconOnlyActions ? '124px' : '230px'}`,
        }}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell className={styles.th}>Zaměstnanec</TableHeaderCell>
              {!isMobile && <TableHeaderCell className={styles.th}>{t('absences.fields.type')}</TableHeaderCell>}
              <TableHeaderCell className={styles.th}>Počátek a Konec</TableHeaderCell>
              {!isMobile && <TableHeaderCell className={styles.thWrap}>{t('absences.fields.totalDays')}</TableHeaderCell>}
              {!isMobile && <TableHeaderCell className={styles.th}>Místo pobytu a poznámka</TableHeaderCell>}
              <TableHeaderCell className={styles.th}>Schválit?</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests?.map(r => (
              <TableRow key={r.id} className={styles.tableRow}>
                <TableCell>{r.employeeFullName}</TableCell>
                {!isMobile && <TableCell className={styles.nowrap}>{r.absenceTypeName}</TableCell>}
                <TableCell className={styles.nowrap}>
                  {formatDateRange(r.startDate, r.endDate)}
                </TableCell>
                {!isMobile && <TableCell className={styles.nowrap}>{String(r.totalDays).replace('.', ',')}{r.dayHalf !== DayHalf.Full ? ` (${DAY_HALF_LABEL[r.dayHalf]})` : ''}</TableCell>}
                {!isMobile && (
                  <TableCell className={styles.nowrap}>
                    {r.location ?? '–'}{r.note ? ` (${r.note})` : ''}
                  </TableCell>
                )}
                <TableCell onClick={e => e.stopPropagation()}>
                  <ActionButtons
                    onApprove={() => decideMutation.mutate({ id: r.id, approved: true })}
                    onReject={() => decideMutation.mutate({ id: r.id, approved: false })}
                    isLoading={decideMutation.isPending}
                    iconOnly={iconOnlyActions}
                  />
                </TableCell>
              </TableRow>
            ))}
            {requests?.length === 0 && (
              <TableRow>
                <TableCell colSpan={isMobile ? 3 : 6} style={{ textAlign: 'center', color: tokens.colorNeutralForeground3 }}>
                  Žádné žádosti čekající na schválení
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </div>
      )}

      {/* Dialog potvrzení přepočtu — výběr roku */}
      <Dialog open={recalcConfirmOpen} onOpenChange={(_, d) => { if (!d.open) setRecalcConfirmOpen(false) }}>
        <DialogSurface style={{ maxWidth: 420 }}>
          <DialogTitle>Přepočítat dovolenou</DialogTitle>
          <DialogBody>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Text>Vyberte rok, pro který chcete přepočítat:</Text>
              <div style={{ display: 'flex', gap: 8 }}>
                {[currentYear - 1, currentYear, currentYear + 1].map(y => (
                  <Button
                    key={y}
                    appearance={recalcYear === y ? 'primary' : 'secondary'}
                    onClick={() => setRecalcYear(y)}
                  >
                    {y}
                  </Button>
                ))}
              </div>
              <Checkbox
                label="Přepočítat i nárok (hodiny)"
                checked={recalcEntitlements}
                onChange={(_, d) => setRecalcEntitlements(!!d.checked)}
              />
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                {recalcEntitlements
                  ? 'Přepočítá čerpání i nárok dle aktuálního úvazku. Používat jednorázově po migraci nebo opravě dat.'
                  : recalcYear > currentYear
                    ? `Pro rok ${recalcYear} se přepočítá čerpání a u HPP 100 % také nárok dle aktuálního nastavení.`
                    : `Pro rok ${recalcYear} se přepočítá pouze čerpání (nárok se nemění).`}
              </Text>
            </div>
          </DialogBody>
          <DialogActions>
            <Button appearance="secondary" onClick={() => setRecalcConfirmOpen(false)}>Zrušit</Button>
            <Button
              appearance="primary"
              icon={recalcMutation.isPending ? <Spinner size="tiny" /> : <ArrowSync24Regular />}
              disabled={recalcMutation.isPending}
              onClick={() => recalcMutation.mutate()}
            >
              Přepočítat
            </Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>

      {/* Dialog výsledku přepočtu */}
      <Dialog open={recalcDialogOpen} onOpenChange={(_, d) => { if (!d.open) setRecalcDialogOpen(false) }}>
        <DialogSurface style={{ maxWidth: 460 }}>
          <DialogTitle>Přepočet dovolené dokončen</DialogTitle>
          <DialogBody>
            {recalcResult && (
              <div>
                <MessageBar intent={recalcResult.errors.length === 0 ? 'success' : 'warning'}>
                  <MessageBarBody>
                    {recalcResult.errors.length === 0
                      ? `Přepočteno ${recalcResult.employeesProcessed} zaměstnanců za rok ${recalcResult.year}${recalcResult.recalcEntitlements ? ' (včetně nároku)' : ''}.`
                      : `Přepočteno ${recalcResult.employeesProcessed} zaměstnanců, ${recalcResult.errors.length} chyb.`}
                  </MessageBarBody>
                </MessageBar>
                {recalcResult.errors.length > 0 && (
                  <ul className={styles.resultList}>
                    {recalcResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                )}
              </div>
            )}
          </DialogBody>
          <DialogActions>
            <Button appearance="primary" onClick={() => setRecalcDialogOpen(false)}>Zavřít</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
